/**
 * Area Tool - Area Creation
 * 
 * Places an Area element at a point (in Area Plan).
 * Features:
 * - Point placement
 * - Automatic boundary detection (Placeholder)
 * - Tag on placement
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface AreaGeometryData {
    position: [number, number, number];
    levelId: string;
    boundary?: [number, number][];
}

export interface AreaElement extends Element {
    category: 'Areas';
    geometryData: AreaGeometryData;
}

export class AreaTool extends BaseTool {
    id = 'area';
    name = 'Area';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Close shape?
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop(); // Remove last duplicate
                this.finishSketch();
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }
    
    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Enter') {
             if (this.points.length > 2) this.finishSketch();
        } else if (event.code === 'Escape') {
            this.points = [];
            this.updatePreview();
        }
    }

    private updatePreview() {
         if (!this.context) return;
         if (this.points.length > 0 && this.currentPoint) {
             const pts = [...this.points, this.currentPoint];
             this.context.appStore.setToolPreview({
                 type: 'polyline',
                 points: pts,
                 color: '#ffdd00' // Area Color
             });
         } else {
             this.context.appStore.setToolPreview(null);
         }
    }

    onPointerUp(): void {}

    private finishSketch() {
        if (!this.context) return;
        const levelId = 'Level 1';
        
        // Calculate center for tag
        const border: [number, number][] = this.points.map(p => [p.x, p.y]);
        const center = this.calculateCentroid(this.points);
        const areaVal = this.calculateArea(this.points);

        const geometryData: AreaGeometryData = {
            position: [center.x, center.y, center.z],
            levelId,
            boundary: border
        };

        const area: AreaElement = {
            id: uuidv4(),
            category: 'Areas',
            type: 'Area',
            parameters: {
                'Level': levelId,
                'Name': 'Area',
                'Area': `${areaVal.toFixed(2)} m²`
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(area);
        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
    
    private calculateCentroid(points: THREE.Vector3[]) {
        let x = 0, y = 0, z = 0;
        points.forEach(p => { x += p.x; y += p.y; z += p.z });
        return new THREE.Vector3(x/points.length, y/points.length, z/points.length);
    }
    
    private calculateArea(points: THREE.Vector3[]) {
        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y;
            area -= points[j].x * points[i].y;
        }
        return Math.abs(area) / 2;
    }
}
